// Core packages
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';

// Third party packages
import { FormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { AuthService } from 'src/app/modules/auth/auth.service';
import { UsersService } from 'src/app/modules/users/users.service';
import { HelperService } from 'src/app/shared/services/helper.service';

// Custom packages

@Component({
  selector: 'app-change-password-dialog',
  templateUrl: './change-password-dialog.component.html',
  styleUrls: ['./change-password-dialog.component.scss']
})
export class ChangePasswordDialogComponent implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  private userId: string;
  // BEGIN - Change password form
  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  // END - ngx-formly
  // END - Change password form

  constructor(
    public dialogRef: MatDialogRef<ChangePasswordDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private translateService: TranslateService,
    private authService: AuthService,
    private usersService: UsersService,
    private toastrService: ToastrService,
    private helperService: HelperService,
  ) {
    dialogRef.disableClose = true; // Disable close by clicking outside of the dialog
  }

  /**
   * Init component
   */
  ngOnInit(): void {
    // Set default icons if none are provided
    if (!this.data.btnOkIcon) {
      this.data.btnOkIcon = 'fa-save';
    }
    if (!this.data.btnCancelIcon) {
      this.data.btnCancelIcon = 'fa-sign-out-alt';
    }

    // Set default button colors if none are provided
    if (!this.data.btnOkColor) {
      this.data.btnOkColor = 'primary';
    }
    if (!this.data.btnCancelIcon) {
      this.data.btnCancelIcon = 'primary';
    }
    this.userId = this.data.user._id;
    this.initForm();
  }

  /**
   * Handle component destroy
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * Init form
   *
   * @since 1.0.0
   */
  initForm(): void {
    // Set model
    this.model = {};

    // Set fields
    this.fields = [
      ...this.fields,
      {
        // see https://stackblitz.com/angular/reveaanjebg
        key: 'newPassword',
        validators: {
          fieldMatch: {
            expression: (control) => {
              const value = control.value;

              return value.confirmPassword === value.password
                // avoid displaying the message error when values are empty
                || (!value.confirmPassword || !value.password);
            },
            message: this.translateService.instant('dialogs.changePassword.password_not_matching'),
            errorPath: 'confirmPassword',
          },
        },
        fieldGroupClassName: 'row',
        fieldGroup: [
          {
            className: 'col-12',
            type: 'input',
            key: 'password',
            templateOptions: {
              label: this.translateService.instant('dialogs.changePassword.password'),
              placeholder: this.translateService.instant('dialogs.changePassword.password'),
              required: true,
              minLength: 5,
              maxLength: 100,
              type: 'password',
            },
          },
          {
            className: 'col-12',
            type: 'input',
            key: 'confirmPassword',
            templateOptions: {
              label: this.translateService.instant('dialogs.changePassword.confirmPassword'),
              placeholder: this.translateService.instant('dialogs.changePassword.confirmPassword'),
              required: true,
              type: 'password',
            },
          },
        ],
      },
    ];
  }

  /**
   * Handle click on denial button
   *
   * @since 1.0.0
   */
  onDenial(): void {
    this.dialogRef.close(false);
    setTimeout(() => {
      this.authService.logout(true);
      return;
    }, 1000);
  }

  /**
   * Handle click on confirmation button
   *
   * @since 1.0.0
   */
  onConfirmation(): void {
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach(key => {
        this.form.controls[key].markAsTouched();
      });
      this.toastrService.error(this.translateService.instant('common.errors.form_is_invalid'));
      return;
    }

    let userData = {
      ...this.data.user,
      password: this.model.newPassword.password,
      updatedBy: this.userId,
    };

    this.usersService.update(this.userId, userData).subscribe(
      res => {
        if (res.status) {
          this.dialogRef.close();
          this.toastrService.success(this.translateService.instant('dialogs.resetPassword.password_reset_successfully'));
          // User has to login with new password (or try again if something went wrong),
          // so let's log him out (after 1 second)
          setTimeout(() => {
            this.authService.logout(true);
            return;
          }, 1000);
        }
      },
      err => {
        this.helperService.handleError(err);
        this.dialogRef.close();
        // this.helperService.handleFormlyError(this.form, err);
      }
    );
  }

}
